import React, { useState } from 'react';
import styled from 'styled-components';
import { getCurrentUser, changePassword } from '../utils/authUtils';
import { ChangePasswordForm } from '../types/user';

const Container = styled.div`
  max-width: 600px;
  margin: 50px auto;
  padding: 20px;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  padding: 24px;
`;

const Title = styled.h2`
  color: #202124;
  margin-bottom: 24px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  color: #202124;
  font-size: 0.875rem;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e1e3e6;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    border-color: #1a73e8;
    box-shadow: 0 0 0 4px rgba(26, 115, 232, 0.1);
    outline: none;
  }
  
  &:hover {
    border-color: #dadce0;
  }
`;

const Button = styled.button<{ isLoading?: boolean }>`
  padding: 14px;
  background: ${props => props.isLoading ? '#ccc' : '#1a73e8'};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: ${props => props.isLoading ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    background: #1557b0;
  }
`;

const Message = styled.div<{ isError?: boolean }>`
  color: ${props => props.isError ? '#dc3545' : '#28a745'};
  padding: 12px;
  border-radius: 8px;
  background-color: ${props => props.isError ? '#f8d7da' : '#d4edda'};
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &::before {
    content: ${props => props.isError ? '"⚠️"' : '"✅"'};
  }
`;

const Settings: React.FC = () => {
  const [formData, setFormData] = useState<ChangePasswordForm>({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });
  const [message, setMessage] = useState<{ text: string; isError: boolean } | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage(null);

    try {
      const currentUser = getCurrentUser();
      if (!currentUser) {
        throw new Error('请先登录');
      }

      if (formData.newPassword !== formData.confirmNewPassword) {
        throw new Error('两次输入的新密码不一致');
      }

      if (formData.newPassword.length < 8) {
        throw new Error('新密码长度不能少于8个字符');
      }

      if (!/[A-Z]/.test(formData.newPassword) || !/[a-z]/.test(formData.newPassword)) {
        throw new Error('新密码必须包含大小写字母');
      }

      if (!/[0-9]/.test(formData.newPassword)) {
        throw new Error('新密码必须包含数字');
      }

      if (!/[^A-Za-z0-9]/.test(formData.newPassword)) {
        throw new Error('新密码必须包含特殊字符');
      }

      await changePassword(currentUser.id, formData.currentPassword, formData.newPassword);
      
      setMessage({ text: '密码修改成功', isError: false });
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: '',
      });
    } catch (error) {
      setMessage({
        text: error instanceof Error ? error.message : '密码修改失败',
        isError: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <Container>
      <Card>
        <Title>修改密码</Title>
        <Form onSubmit={handleSubmit}>
          <FormField>
            <Label>当前密码</Label>
            <Input
              type="password"
              name="currentPassword"
              placeholder="请输入当前密码"
              value={formData.currentPassword}
              onChange={handleChange}
              required
            />
          </FormField>

          <FormField>
            <Label>新密码</Label>
            <Input
              type="password"
              name="newPassword"
              placeholder="请输入新密码"
              value={formData.newPassword}
              onChange={handleChange}
              required
            />
          </FormField>

          <FormField>
            <Label>确认新密码</Label>
            <Input
              type="password"
              name="confirmNewPassword"
              placeholder="请再次输入新密码"
              value={formData.confirmNewPassword}
              onChange={handleChange}
              required
            />
          </FormField>

          <Button type="submit" isLoading={isLoading} disabled={isLoading}>
            {isLoading ? '修改中...' : '修改密码'}
          </Button>
        </Form>

        {message && (
          <Message isError={message.isError}>
            {message.text}
          </Message>
        )}
      </Card>
    </Container>
  );
};

export default Settings; 